import React from 'react';
import usdaLogoDiagram from 'cd-eft/assets/images/usda-logo-name.png';
import { Table } from 'reactstrap';
import { formatDateTime, formatMonthDayYear, formatMonthDayYear2 } from 'cd-eft/utils/formatDate';
import { emailToName } from 'cd-eft/utils/stringUtilities';
import stores from 'cd-eft/models/Stores';
import { observer } from 'mobx-react-lite';
import { toTitleFromUnderscore, updateNameAddSpace } from '../../../../src/utils/stringUtilities';
import './TerraceDesignReport.scss';

const { designStore, terraceDesignStore } = stores.AppStore;

export const TerraceDesignReport = observer((props: any) => {
  const getPageMargins = () => {
    return `@page { margin: ${0.5}in !important; }`;
  };

  const stationTableConfig = [
    'Status',
    'Error Code',
    'Station (ft)',
    'Ground Z (ft)',
    'Channel Z (ft)',
    'Cut/ Fill (ft)',
    'Length (ft)',
    'Slope (%)',
    'Front Height (ft)',
    'Flow Q (cfs)',
    'Flow Velocity (ft/sec)',
    'Flow Depth (ft)',
    'Design Height (ft)',
    'Drain / Block',
  ];

  const project = props.project ? props.project : designStore.projectsStore.currentProject;
  const terrace = terraceDesignStore.terraceDesignObject;

  const safeValue = (val, fallback = '') => {
    if (val === null || val === undefined) return fallback;
    if (typeof val === 'number') {
      return parseFloat(val.toFixed(2)).toString(); //2 decimals and removing trailing zeros
    }
    return toTitleFromUnderscore(val);
  };
  return (
    <div className="design-report-container terrace-report page-break">
      <style type="text/css" media="print">
        {'\
        @page { size: landscape; }\
      '}
      </style>
      <style>{getPageMargins()}</style>
      <div className="header">
        <div className="logo">
          <img id="usda-logo" src={usdaLogoDiagram} alt="usda logo" />
        </div>
        <div>
          <div className="date">Report Generated:</div>
          <div>{formatDateTime(new Date())}</div>
        </div>
      </div>
      <div className="form-heading">
        <h1 className="h1">Terrace Design Documentation</h1>
      </div>
      <div className="form-content">
        <div className="form-columns">
          <div className="column1">
            <div className="form-row">
              <div className="form-title">Project Name:</div>
              <div className="form-value">{project.projectTitle}</div>
            </div>
            <div className="form-row">
              <div className="form-title">Project Status:</div>
              <div className="form-value">
                {project.projectStatus ? project.projectStatus[0].toUpperCase() + project.projectStatus.slice(1) : null}
              </div>
            </div>
            <div className="form-row">
              <div className="form-title extend-tab1">Location:</div>
              <div className="form-value">
                &nbsp;&nbsp;&nbsp;&nbsp;{project.county}, {project.state}
              </div>
            </div>
          </div>
          <div className="column1">
            <div className="form-row">
              <div className="form-title">Designed by:&nbsp;&nbsp;</div>
              <div className="form-value">{emailToName(project.designedBy)}</div>
            </div>
            <div className="form-row">
              <div className="form-title">Checked by:&nbsp;&nbsp;&nbsp;</div>
              <div className={`form-value ${props.formData && props.formData.checkedByName ? '' : 'underline-value-13'}`}>
                {props.formData && props.formData.checkedByName ? props.formData.checkedByName : null}
              </div>
            </div>
            <div className="form-row">
              <div className="form-title">Approved by:&nbsp;</div>
              <div className={`form-value ${props.formData && props.formData.approvedByName ? '' : 'underline-value-13'}`}>
                {props.formData && props.formData.approvedByName ? props.formData.approvedByName : null}
              </div>
            </div>
          </div>
          <div className="column1">
            <div className="form-row ">
              <div className="form-title">Date:</div>
              <div className="form-value form-date-value">
                {props.project.lastChangedDate ? formatMonthDayYear2(props.project.lastChangedDate) : ''}
              </div>
            </div>
            <div className="form-row">
              <div className="form-title">Date:</div>
              <div className={`form-value form-date-value ${props.formData && props.formData.checkedByDate ? '' : 'underline-value-13'}`}>
                {props.formData && props.formData.checkedByDate ? formatMonthDayYear2(props.formData.checkedByDate) : ''}
              </div>
            </div>
            <div className="form-row">
              <div className="form-title">Date:</div>
              <div
                className={`form-value form-date-value ${props.formData && props.formData.approvedByDate ? '' : 'underline-value-13'}`}
              >
                {props.formData && props.formData.approvedByDate ? formatMonthDayYear2(props.formData.approvedByDate) : ''}
              </div>
            </div>
          </div>
        </div>
        <div className="button-header">Design Summary</div>
        <div className="light-teal-banner usa-summary-box mt-0">
          <div className="column">
            <div className="header">Terrace Name:</div>
            <div className="value">{terrace.alignedFeatureName ? terrace.alignedFeatureName : props.reportData.designSummary?.terraceName}</div>
          </div>
          <div className="column">
            <div className="header">Terrace Type:</div>
            <div className="value">{safeValue(props.reportData.designSummary?.terraceType)}</div>
          </div>
          <div className="column">
            <div className="header">Design Surface Type:</div>
            <div className="value">{safeValue(props.reportData.designSummary?.designSurfaceType)}</div>
          </div>
          <div className="column">
            <div className="header peakQ">Underground Outlet Drain Fraction:</div>
            <div className="value">{props.reportData.designSummary?.undergroundOutletDrainFraction === 0 ? 'N/A' :
              `${safeValue(props.reportData.designSummary?.undergroundOutletDrainFraction)}%`}</div>
          </div>
        </div>

        <div className="button-header">Hydrology</div>
        <div className="light-blue-border">
          <div>
            <div className="text-bold">Drainage Area (ac):</div>
            <div className="form-value col">{safeValue(props.reportData.hydrologySection?.drainageArea)}</div>
          </div>
          <div>
            <div className="text-bold">Weighted Curve Number:</div>
            <div className="form-value col">{safeValue(props.reportData.hydrologySection?.weightedCurveNumber)}</div>
          </div>
          <div>
            <div className="text-bold">Watershed Length (ft):</div>
            <div className="form-value col">{safeValue(props.reportData.hydrologySection?.watershedLength)}</div>
          </div>
          <div>
            <div className="text-bold">Watershed Slope (%):</div>
            <div className="form-value col">{safeValue(props.reportData.hydrologySection?.watershedSlope)}</div>
          </div>
          <div>
            <div className="text-bold">Runoff (in):</div>
            <div className="form-value col">{safeValue(props.reportData.hydrologySection?.runoff)}</div>
          </div>
          <div>
            <div className="text-bold">Peak Flow Q (cfs):</div>
            <div className="form-value col">{safeValue(props.reportData.hydrologySection?.peakFlowQ)}</div>
          </div>
        </div>

        <div className="button-header">Design Statistics</div>
        <div className="light-blue-border">
          <div>
            <div className="text-bold">Terrace Length (ft):</div>
            <div className="form-value col">{safeValue(props.reportData.designStatistics?.terraceLength)}</div>
          </div>
          <div>
            <div className="text-bold">Flood Routing Model:</div>
            <div className="form-value col">{props.reportData.designStatistics?.floodRoutingModel ? updateNameAddSpace(props.reportData.designStatistics.floodRoutingModel) : ''}</div>
          </div>
          <div>
            <div className="text-bold">Total Cut (cy):</div>
            <div className="form-value col">{safeValue(props.reportData.designStatistics?.totalCut)}</div>
          </div>
          <div>
            <div className="text-bold">Drainage/Basin Area (ac):</div>
            <div className="form-value col">{safeValue(props.reportData.designStatistics?.floodArea)}</div>
          </div>
          <div>
            <div className="text-bold">Water Elevation (ft):</div>
            <div className="form-value col">{safeValue(props.reportData.designStatistics?.waterElevation)}</div>
          </div>
          <div>
            <div className="text-bold">Total Fill (cy):</div>
            <div className="form-value col">{safeValue(props.reportData.designStatistics?.totalFill)}</div>
          </div>
          <div>
            <div className="text-bold">Flood Duration (hr):</div>
            <div className="form-value col">{safeValue(props.reportData.designStatistics?.floodDuration)}</div>
          </div>
          <div>
            <div className="text-bold">Required Storage (ac-in):</div>
            <div className="form-value col">{safeValue(props.reportData.designStatistics?.requiredStorage)}</div>
          </div>
          <div>
            <div className="text-bold">Cut/Fill Balance (cy):</div>
            <div className="form-value col">{safeValue(props.reportData.designStatistics?.cutFillBalance)}</div>
          </div>
        </div>

        <div className="button-header">Inputs</div>
        <div className="block-no-border">
          <div>
            <div className="text-bold">Terrace Name:</div>
            <div className="form-value col">{props.reportData.inputs?.terraceName}</div>
          </div>
          <div>
            <div className="text-bold">Terrace Type:</div>
            <div className="form-value col">{safeValue(props.reportData.inputs?.terraceType)}</div>
          </div>
          <div>
            <div className="text-bold">Alignment:</div>
            <div className="form-value col">{safeValue(props.reportData.inputs?.alignment)}</div>
          </div>
          <div>
            <div className="text-bold">Stripping Depth (in):</div>
            <div className="form-value col">{safeValue(props.reportData.inputs?.strippingDepth)}</div>
          </div>
          <div>
            <div className="text-bold">Subbasin :</div>
            <div className="form-value col">{safeValue(props.reportData.inputs?.subbasin)}</div>
          </div>
          <div>
            <div className="text-bold">Frequency :</div>
            <div className="form-value col">{safeValue(props.reportData.inputs?.frequency)}</div>
          </div>
          <div>
            <div className="text-bold">Dimensionless Unit Hydrograph:</div>
            <div className="form-value col long-col">{safeValue(props.reportData.inputs?.dimensionlessUnitHydrograph)}</div>
          </div>
          <div>
            <div className="text-bold">Drainage Area (ac) :</div>
            <div className="form-value col">{safeValue(props.reportData.inputs?.drainageArea)}</div>
          </div>
          <div>
            <div className="text-bold">Curve Number :</div>
            <div className="form-value col">{safeValue(props.reportData.inputs?.curveNumber)}</div>
          </div>
          <div>
            <div className="text-bold">Watershed Length (ft) :</div>
            <div className="form-value col">{safeValue(props.reportData.inputs?.watershedLength)}</div>
          </div>
          <div>
            <div className="text-bold">Watershed Slope (%) :</div>
            <div className="form-value col">{safeValue(props.reportData.inputs?.watershedSlope)}</div>
          </div>
          <div>
            <div className="text-bold">Erodibility:</div>
            <div className="form-value col">{safeValue(props.reportData.inputs?.erodibility)}</div>
          </div>
          <div>
            <div className="text-bold">Vegetal Cover Type :</div>
            <div className="form-value col">{safeValue(props.reportData.inputs?.vegetalCoverType)}</div>
          </div>
          <div>
            <div className="text-bold">Retardance Class for Stability:</div>
            <div className="form-value col">{safeValue(props.reportData.inputs?.retardanceClassStability)}</div>
          </div>
          <div>
            <div className="text-bold">Retardance Class for Capacity:</div>
            <div className="form-value col">{safeValue(props.reportData.inputs?.retardanceClassCapacity)}</div>
          </div>
          <div>
            <div className="text-bold">Flood Routing Model :</div>
            <div className="form-value col">{props.reportData.inputs?.floodRoutingModel ? updateNameAddSpace(props.reportData.inputs.floodRoutingModel) : ''}</div>
          </div>
          <div>
            <div className="text-bold">Percentage Stored (%) :</div>
            <div className="form-value col">{safeValue(props.reportData.inputs?.percentageStored)}</div>
          </div>
          <div>
            <div className="text-bold">Flood Duration (hr) :</div>
            <div className="form-value col">{safeValue(props.reportData.inputs?.floodDuration)}</div>
          </div>
          <div>
            <div className="text-bold">Design Life (yr) :</div>
            <div className="form-value col">{safeValue(props.reportData.inputs?.designLife)}</div>
          </div>
          <div>
            <div className="text-bold">Erosion Rate (T/ac-yr) :</div>
            <div className="form-value col">{safeValue(props.reportData.inputs?.erosionRate)}</div>
          </div>
          <div>
            <div className="text-bold">Trap Efficiency :</div>
            <div className="form-value col">{safeValue(props.reportData.inputs?.trapEfficiency)}</div>
          </div>
          <div>
            <div className="text-bold">Sediment Density (T/ac-yr):</div>
            <div className="form-value col">{safeValue(props.reportData.inputs?.sedimentDensity)}</div>
          </div>
        </div>

        <div className="button-header">Template</div>
        <div className="block-no-border">
          <div>
            <div className="text-bold">Uphill Direction:</div>
            <div className="form-value col">{safeValue(props.reportData.template?.uphillDirection)}</div>
          </div>
          <div>
            <div className="text-bold">Flag Line Location:</div>
            <div className="form-value col">{safeValue(props.reportData.template?.flagLineLocation)}</div>
          </div>
          <div>
            <div className="text-bold">Offset from Alignment (ft):</div>
            <div className="form-value col">{safeValue(props.reportData.template?.offsetFromAlignment)}</div>
          </div>
          <div>
            <div className="text-bold">Bottom Width (ft):</div>
            <div className="form-value col">{safeValue(props.reportData.template?.bottomWidth)}</div>
          </div>
          <div>
            <div className="text-bold">Front Height (ft):</div>
            <div className="form-value col">{safeValue(props.reportData.template?.frontHeight)}</div>
          </div>
          <div>
            <div className="text-bold">Top Width (ft):</div>
            <div className="form-value col">{safeValue(props.reportData.template?.topWidth)}</div>
          </div>
          <div>
            <div className="text-bold">Cut Slope ({props.reportData.template?.cutSlopeMode === 1 ? 'ft/ft' : 'ft'}):</div>
            <div className="form-value col">{safeValue(props.reportData.template?.cutSlope)}</div>
          </div>
          <div>
            <div className="text-bold">Front Slope ({props.reportData.template?.frontSlopeMode === 1 ? 'ft/ft' : 'ft'}):</div>
            <div className="form-value col">{safeValue(props.reportData.template?.frontSlope)}</div>
          </div>
          <div>
            <div className="text-bold">Back Slope ({props.reportData.template?.backSlopeMode === 1 ? 'ft/ft' : 'ft'}):</div>
            <div className="form-value col">{safeValue(props.reportData.template?.backSlope)}</div>
          </div>
          <div>
            <div className="text-bold">Overfill Mode:</div>
            <div className="form-value col">{safeValue(props.reportData.template?.overfillMode)}</div>
          </div>
          <div>
            <div className="text-bold">Overfill Amount:</div>
            <div className="form-value col">{safeValue(props.reportData.template?.overfillAmount)}</div>
          </div>
          <div>
            <div className="text-bold">Freeboard Mode:</div>
            <div className="form-value col">{safeValue(props.reportData.template?.freeboardMode)}</div>
          </div>
          <div>
            <div className="text-bold">Freeboard (ft) :</div>
            <div className="form-value col">{safeValue(props.reportData.template?.freeboard)}</div>
          </div>
          <div>
            <div className="text-bold">Embankment Start Slope ({props.reportData.template?.embankmentStartSlopeMode === 'RATIO_BASED' ? 'ft/ft' : 'ft'}):</div>
            <div className="form-value col">{safeValue(props.reportData.template?.embankmentStartSlopeValue)}</div>
          </div>
          <div>
            <div className="text-bold">Embankment Start Slope: Limit (ft):</div>
            <div className="form-value col">{safeValue(props.reportData.template?.embankmentStartSlopeLimit)}</div>
          </div>
          <div>
            <div className="text-bold"></div>
            <div className="form-value col"></div>
          </div>
        </div>

        <div className="button-header">Station Table</div>
        <div className="veg-table">
          <Table className="usa-table usa-table--borderless wd-report-table">
            <thead>
              <tr className="narrow-td">
                {stationTableConfig.map((el, i) => (
                  <th key={`stationTable-th-${i}`} className="narrow-td">
                    {el}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {props.reportData.stationsTable && props.reportData.stationsTable.length > 0
                ? props.reportData.stationsTable.map((obj: any, index) => (
                  <tr className="" key={'key-waterwayvertexParam--' + index}>
                    <td className="narrow-tr">
                      <div className="status-container">
                        <p
                          className={obj.status ? `status-label status--${obj.status}` : ''}
                          aria-label={`status-0`}
                          data-testid={`status-0`}
                        >
                          <span>{obj.status?.split('_')[0]}</span>
                        </p>
                      </div>
                    </td>
                    <td className="narrow-tr">{obj.errorCode ? obj.errorCode : 'N/A'}</td>
                    <td className="narrow-tr">{safeValue(obj.station)}</td>
                    <td className="narrow-tr">{safeValue(obj.groundZ)}</td>
                    <td className="narrow-tr">{safeValue(obj.channelZ)}</td>
                    <td className="narrow-tr">{safeValue(obj.cutFill)}</td>
                    <td className="narrow-tr">{safeValue(obj.length)}</td>
                    <td className="narrow-tr">{safeValue(obj.slope * 100)}</td>
                    <td className="narrow-tr">{safeValue(obj.frontHeight)}</td>
                    <td className="narrow-tr">{obj.flowQ ? obj.flowQ.toFixed(2) : 0}</td>
                    <td className="narrow-tr">{safeValue(obj.stabilityVelocity)}</td>
                    <td className="narrow-tr">{obj.velocityFlowDepth ? obj.velocityFlowDepth.toFixed(2) : 0}</td>
                    <td className="narrow-tr">{safeValue(obj.designHeight.toFixed(1))}</td>
                    <td className="narrow-tr">{safeValue(obj.drain)}</td>
                  </tr>
                ))
                : null}
            </tbody>
          </Table>

          <h2 className="button-header">Additional Information</h2>
        </div>
      </div>
    </div>
  );
});
